// ARCA-93 / WS2 — client anon practice-session mint (POST /api/auth/anon).
//
// "Mint the anon session on entering practice, not in the WS1 route guard."
// A guest who taps "Practice vs the bot" needs a device-local anon JWT before
// the bot match-create call so the backend (which requires a bearer) accepts
// it. We mint lazily — only when there is no usable session at all — so an
// authenticated user practising vs the bot keeps their real identity.
//
// Bootstrap-handshake shape mirrors auth/exchange.ts: raw `fetch`, NOT
// `apiFetch`. This call carries NO Authorization (it IS the session-creation
// step) and must not run through apiFetch's 401→silent-refresh→clearAuth
// machinery, which is meaningless for an unauthenticated mint.

import { effectiveBearerJwt, saveAnonSession } from '../stores/anonSession.js'
import { AuthTransientError } from './types.js'

const V1 = 'application/vnd.runegambit.v1+json'

interface AnonMintResponse {
  anon_jwt: string
  // ms since epoch — backend mints `Date.now() + ANON_TTL_MS`
  // (apps/backend/src/auth/anon-session.ts); maps straight to exp_ms.
  expires_at: number
}

function getApiBaseUrl(): string {
  const url = import.meta.env?.VITE_API_BASE_URL
  if (!url || url.startsWith('<')) {
    throw new AuthTransientError(0, 'VITE_API_BASE_URL not configured')
  }
  return url
}

/**
 * Mint a fresh anonymous practice session and persist it to $anonBearer.
 *
 * Throws AuthTransientError on network failure or any non-2xx response — the
 * caller (BotTierModal) surfaces a "couldn't start a bot match" message and
 * lets the user retry.
 */
export async function mintAnonSession(): Promise<void> {
  const url = `${getApiBaseUrl()}/api/auth/anon`

  let response: Response
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: { Accept: V1 },
    })
  } catch {
    throw new AuthTransientError(0, 'Network error during /api/auth/anon')
  }

  if (!response.ok) {
    throw new AuthTransientError(
      response.status,
      `Unexpected status ${response.status} from /api/auth/anon`,
    )
  }

  const body = (await response.json()) as AnonMintResponse
  saveAnonSession(body.anon_jwt, body.expires_at)
}

/**
 * Ensure a usable session exists before entering bot practice. No-op when a
 * real authenticated bearer OR a still-valid anon bearer is already present;
 * otherwise mints a fresh anon session.
 */
export async function ensureAnonSession(): Promise<void> {
  if (effectiveBearerJwt() !== null) return
  await mintAnonSession()
}
